import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
import { PollBar } from './PollBar';
import { ExpandableCard } from './ExpandableCard';
export function RewardSlide({
  topText,
  pollPercentage,
  pollText,
  rolePolls,
  insight,
  pros,
  cons,
  expandableTitle,
  expandableContent,
  expandableBgImage,
  visualization,
  onNext,
  onBack,
  onSkip
}: {
  topText?: string;
  pollPercentage: number;
  pollText: string;
  rolePolls?: {
    id: string;
    shortName: string;
    percentage: number;
    text: string;
  }[];
  insight: string[];
  pros?: string[];
  cons?: string[];
  expandableTitle?: string;
  expandableContent?: React.ReactNode;
  expandableBgImage?: string;
  visualization?: React.ReactNode;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedRole, setSelectedRole] = useState(0);
  const currentPoll = rolePolls ? rolePolls[selectedRole] : {
    percentage: pollPercentage,
    text: pollText
  };
  const hasProsCons = pros && pros.length > 0 || cons && cons.length > 0;
  const hasExpandable = !!(expandableTitle && expandableContent);
  return <div className="flex flex-col h-full px-6 py-8 relative z-10 bg-[#0B1623]" data-id="element-138">
      <div className="flex items-center justify-between mb-6" data-id="element-139">
        <button onClick={onBack} className="p-2 -ml-2 text-slate-500 hover:text-white transition-colors z-20" aria-label="Go back" data-id="element-140">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-141" />
        </button>
        <div className="bg-white px-3 py-1.5 rounded-lg inline-block shadow-sm" data-id="element-142">
          <img src="https://cdn.magicpatterns.com/uploads/ifejLUcSApynCwwp5oN3Zr/Screenshot_2026-03-04_at_16.40.29.png" alt="Minerva Imaging" className="h-4 object-contain" data-id="element-143" />
        </div>
        {onSkip ? <button onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2" data-id="element-144">
            Skip
          </button> : <div className="w-10" data-id="element-145" />}
      </div>

      <div className="flex-1 flex flex-col overflow-y-auto pb-4 no-scrollbar" data-id="element-146">
        {topText && <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-sm font-medium text-slate-500 uppercase tracking-widest mb-6" data-id="element-147">
            {topText}
          </motion.p>}

        {rolePolls && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-wrap gap-1 mb-4 bg-white/5 p-1 rounded-lg border border-white/10" data-id="element-148">
            {rolePolls.map((role, idx) => <button key={role.id} onClick={() => setSelectedRole(idx)} className={`flex-1 min-w-[70px] py-1.5 px-1 text-[10px] sm:text-xs font-medium rounded-md transition-all duration-200 ${selectedRole === idx ? 'bg-[#4B9FE1] text-white shadow-sm' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`} data-id="element-149">
                {role.shortName}
              </button>)}
          </motion.div>}

        <PollBar percentage={currentPoll.percentage} text={currentPoll.text} data-id="element-150" />

        {visualization && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="mb-6" data-id="element-151">
            {visualization}
          </motion.div>}

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-8 space-y-4" data-id="element-152">
          {insight.map((paragraph, i) => <p key={i} className="text-base sm:text-lg text-white leading-relaxed font-light" data-id="element-153">
              {paragraph}
            </p>)}
        </motion.div>

        {hasProsCons && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white/5 rounded-xl p-5 border border-white/10 shadow-sm mb-8 backdrop-blur-sm" data-id="element-154">
            <div className="grid grid-cols-1 gap-3" data-id="element-155">
              {pros?.map((pro, i) => <div key={`pro-${i}`} className="flex items-start" data-id="element-156">
                  <span className="text-[#4B9FE1] mr-3 mt-0.5 text-lg leading-none" data-id="element-157">
                    •
                  </span>
                  <span className="text-sm text-slate-300" data-id="element-158">{pro}</span>
                </div>)}
              {cons?.map((con, i) => <div key={`con-${i}`} className="flex items-start" data-id="element-159">
                  <span className="text-slate-600 mr-3 mt-0.5 text-lg leading-none" data-id="element-160">
                    •
                  </span>
                  <span className="text-sm text-slate-400" data-id="element-161">{con}</span>
                </div>)}
            </div>
          </motion.div>}

        {hasExpandable && <motion.button initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} onClick={() => setIsExpanded(true)} className="group relative w-full rounded-xl overflow-hidden aspect-[21/9] bg-white/5 border border-white/10 mt-auto mb-6 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#4B9FE1] focus:ring-offset-2 focus:ring-offset-[#0B1623]" data-id="element-162">
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1623] to-transparent z-10" data-id="element-163" />

            {expandableBgImage ? <img src={expandableBgImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-164" /> : <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#4B9FE1] via-transparent to-transparent" data-id="element-165" />}

            <div className="absolute inset-0 flex items-center justify-center z-20" data-id="element-166">
              <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-[#4B9FE1] transition-all duration-300" data-id="element-167">
                <PlayIcon className="w-5 h-5 text-white ml-1" data-id="element-168" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4 z-20 text-left" data-id="element-169">
              <p className="text-white text-sm font-medium truncate" data-id="element-170">
                {expandableTitle}
              </p>
            </div>
          </motion.button>}
      </div>

      <motion.button initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.6
    }} onClick={onNext} className="w-full py-4 bg-[#4B9FE1] text-white rounded-xl font-medium flex items-center justify-center hover:bg-[#3A8BD0] transition-colors focus:outline-none focus:ring-2 focus:ring-[#4B9FE1] focus:ring-offset-2 focus:ring-offset-[#0B1623] mt-4" data-id="element-171">
        Next
        <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-172" />
      </motion.button>

      {hasExpandable && <ExpandableCard isOpen={isExpanded} onClose={() => setIsExpanded(false)} title={expandableTitle!} data-id="element-173">
          {expandableContent}
        </ExpandableCard>}
    </div>;
}